import { useParams } from "react-router-dom";
import axios from "../../../../axios";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  ShoppingCart,
  CreditCard,
  Shield,
  Zap,
  ArrowRight,
  Tag,
  Clock,
  Award,
  Sparkles,
} from "lucide-react";

function Checkoutpage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [data, setData] = useState({});

  useEffect(() => {
    async function fetchData() {
      setPageLoading(true);
      try {
        const response = await axios.get("getAllCourses");
        if (response?.data) {
          const result = response?.data?.result.find((el) => el._id === id);
          if (result) {
            setData(result);
          } else {
            console.error("No course found with id:", id);
          }
        }
      } catch (error) {
        console.error("Error fetching course:", error);
      } finally {
        setPageLoading(false);
      }
    }
    fetchData();
  }, [id]);

  const handlePayment = () => {
    const paymentData = {
      courseId: id,
    };
    setLoading(true);
    axios
      .post("createOrder", paymentData)
      .then((response) => {
        if (response?.data) {
          window.location.href = response.data?.result?.upiLink;
        }
      })
      .catch((error) => {
        console.error("Payment error:", error);
        setLoading(false);
      });
  };

  const discountPercentage =
    data.price && data.offerPrice
      ? Math.round(((data.price - data.offerPrice) / data.price) * 100)
      : 0;

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#2A1B3D] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm">Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2A1B3D] pt-16 pb-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-[400px] bg-gradient-to-b from-purple-900/20 to-transparent pointer-events-none"></div>
      <div className="absolute top-[-100px] right-[-100px] w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <ShoppingCart size={28} className="text-purple-400" />
            <h1 className="text-3xl md:text-4xl font-bold text-white">
              Checkout
            </h1>
          </div>
          <p className="text-gray-400 text-sm md:text-base">
            Complete your purchase and start learning today
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* LEFT: Course Details */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="bg-[#3F234E] rounded-3xl border border-white/5 overflow-hidden">
              {/* Course Header */}
              <div className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 p-6 border-b border-white/5">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/30 to-indigo-500/30 flex items-center justify-center border border-white/10">
                    <img
                      src={data.planImage}
                      alt={data.planName}
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-1">
                      {data.planName}
                    </h2>
                    <div className="flex items-center gap-3">
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-bold text-white">
                          ₹{data.offerPrice}
                        </span>
                        {data.price > data.offerPrice && (
                          <span className="text-lg text-gray-500 line-through">
                            ₹{data.price}
                          </span>
                        )}
                      </div>
                      {discountPercentage > 0 && (
                        <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Tag size={12} />
                          {discountPercentage}% OFF
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Content */}
              <div className="p-6">
                {/* Exams Covered */}
                {data?.exams?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                      <Award size={16} className="text-purple-400" />
                      Exams Covered
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {data.exams.map((exam, i) => (
                        <span
                          key={exam._id || i}
                          className="px-3 py-1.5 bg-purple-500/20 border border-purple-500/30 text-purple-200 text-xs font-medium rounded-lg"
                        >
                          {exam.examName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                <div>
                  <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
                    <Sparkles size={16} className="text-purple-400" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "10,000+ Exam-Oriented Questions",
                      "Unlimited Practice Sessions",
                      "Performance Based Rewards",
                      "Lifetime Validity",
                      "24x7 Priority Support",
                    ].map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-3 text-gray-300 text-sm"
                      >
                        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="bg-[#3F234E] rounded-3xl border border-white/5 overflow-hidden sticky top-6">
              {/* Summary Header */}
              <div className="bg-gradient-to-r from-purple-600/20 to-indigo-600/20 p-6 border-b border-white/5">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <CreditCard size={20} className="text-purple-400" />
                  Order Summary
                </h3>
              </div>

              <div className="p-6">
                {/* Pricing Breakdown */}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center text-gray-300">
                    <span className="text-sm">Original Price</span>
                    <span className="text-sm line-through text-gray-500">
                      ₹{data.price}
                    </span>
                  </div>

                  {discountPercentage > 0 && (
                    <div className="flex justify-between items-center text-green-400">
                      <span className="text-sm font-medium">
                        Discount ({discountPercentage}%)
                      </span>
                      <span className="text-sm font-medium">
                        -₹{data.price - data.offerPrice}
                      </span>
                    </div>
                  )}

                  <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>

                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-white">Total</span>
                    <span className="text-2xl font-bold text-white">
                      ₹{data.offerPrice}
                    </span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="mb-6">
                  <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                    <Shield size={14} className="text-purple-400" />
                    Secure Payment
                  </h4>
                  <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                    <p className="text-gray-400 text-xs text-center">
                      UPI • Cards • Net Banking
                    </p>
                  </div>
                </div>

                {/* Pay Button */}
                {loading ? (
                  <div className="flex justify-center py-6">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-gray-400 text-sm">Processing...</p>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handlePayment}
                    className="w-full py-4 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold text-base shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 group"
                  >
                    <Zap
                      size={20}
                      className="fill-white group-hover:animate-pulse"
                    />
                    Pay ₹{data.offerPrice}
                    <ArrowRight
                      size={18}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </button>
                )}

                {/* Security Note */}
                <div className="mt-4 flex items-center justify-center gap-2 text-gray-500 text-xs">
                  <Shield size={12} />
                  <span>100% Secure & Protected</span>
                </div>

                {/* Validity Badge */}
                <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-xl p-3 flex items-center gap-2">
                  <Clock size={16} className="text-green-400 flex-shrink-0" />
                  <p className="text-green-300 text-xs font-medium">
                    Lifetime Access • No Expiry
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Checkoutpage;
